<template>
	<el-row>
		<el-col :span="18">
			<div class="base-form header">
				<h2>Список заказ-нарядов</h2>
				<el-button type="primary" size="large" @click="onCreateOrderClick">					
					<el-icon :size="20"><CirclePlus /></el-icon>
					&nbsp;
					Новый заказ-наряд
				</el-button>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="6">
			<div class="base-form filter">
				<el-input
					v-model="ordersSearchFilter"	
					clearable	
					max="250"
					placeholder="Поиск заказ-нарядов по номеру, контрагенту, менеджеру, VIN или гос. номеру"
					@input="fetchOrders">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="6">
			<div class="base-form filter">
				<el-date-picker
					style="box-sizing: border-box;"
					v-model="ordersDateRangeFilter"
					:clearable="false"
					type="daterange"
					range-separator="-"
					start-placeholder="Дата начала"
					end-placeholder="Дата окончания"
					format="DD.MM.YYYY"
					@change="fetchOrders"
				/>				
				<div class="lr-margin-container">
					<el-link v-for="f in predefinedDatesRangeFilters" :underline="false" :key="f.days" :type="f.active ? 'primary' : 'info'" @click="() => onPredefinedDateRangeFilterClick(f.days)">
						{{ f.label }}
					</el-link>
				</div>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="5">
			<div class="base-form filter">
				<el-select 				
					v-model="ordersManagersFilter" 
					placeholder="Фильтровать заказ-наряды по менеджерам" 
					@change="fetchOrders"
					:multiple="true">
					<el-option v-for="m in managers"
						:key="m.id"
						:value="m.id"
						:label="`${m.lastname} ${m.firstname.substring(0,1)}.`">
					</el-option>
				</el-select>		
			</div>
		</el-col>
		<el-col :span="5">
			<div class="base-form filter">
				<el-select 
					v-model="ordersStatusesFilter" 
					:multiple="true" 
					placeholder="Фильтровать заказ-наряды по статусам"
					@change="fetchOrders"
					collapse-tags
					collapse-tags-tooltip>
					<template #label="{ value }">
						{{ statusesDescriptions[value] }}
					</template>
					<el-option
						v-for="item in statuses"
						:key="item.label"
						:value="item.value">
						{{ item.label }}
					</el-option>
				</el-select>
			</div>
		</el-col>
		<el-col :span="5">
			<div class="base-form filter">
				<el-select
					v-model="ordersBranchesFilter"
					clearable
					:multiple="true"
					placeholder="Фильтр по подразделению"
					@change="fetchOrders">
					<el-option v-for="p in employerBranches"
						:key="p.id"
						:value="p.id"
						:label="p.name">
					</el-option>
				</el-select>
			</div>
		</el-col>
		<el-col :span="5">
			<div class="base-form filter">
				<counterparty-filter-component ref="counterpartyFilterComponent" @selected="onCounterpartyFilterChanged" />
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="24">			
			<scrollview-component min-height="220">
				<template #default="{ height }">					
					<el-table
						ref="ordersTable"
						:height="height"
						v-loading="tableLoading"
						show-summary
						:data="ordersList"
						highlight-current-row
						:summary-method="calculateTotal"
						@row-dblclick="onTableRowDblClick"
						@sort-change="onTableSortChange"
						:default-sort="{ prop: tableSortColumn, order: tableSortDir }">
						<template #empty>
							<el-text type="info">Список заказ-нарядов пуст ☹️</el-text>
						</template>
						<el-table-column sortable prop="date" column-key="date" label="Дата">
							<template #default="scope">
								{{ dateFormatter(scope.row.date, true) }}
							</template>
						</el-table-column>
						<el-table-column sortable prop="number" label="№" column-key="number">					
							<template #default="scope">
								<el-link 
									:underline="false" 
									@click="(e) => onTableOrderNumberClick(scope.row, e)"
									type="primary">
									{{ scope.row.number }}
								</el-link>
							</template>
						</el-table-column>
						<el-table-column sortable column-key="statusid" label="Статус">
							<template #default="scope">
									<order-status-selector-component
										:parent-scrollable-element="tableScollElement"
										:forward-flow="true"
										:cancellable="true"
										:disabled="scope.row.isAsyncOperationPending"
										:status="scope.row.status"
										@change="(newStatusId) => onTableOrderStatusChange(scope.row, newStatusId)"
									/>
							</template>
						</el-table-column>
						<el-table-column prop="manager" label="Менеджер" sortable column-key="managerId">
							<template #default="scope">
								<el-link type="primary" @click="(e) => onTableManagerCellClick(e, scope.row.manager)">{{ `${scope.row.manager.lastname} ${scope.row.manager.firstname?.substring(0,1)}.` }}</el-link>
							</template>
						</el-table-column>
						<el-table-column prop="customer" label="Заказчик" sortable column-key="customerId">
							<template #default="scope">
								<el-link type="primary" v-if="!scope.row.customer?.legalEntity" @click="(e) => onTableCustomerCellClick(e, scope.row.customer)">
									{{ `${scope.row.customer.lastname} ${scope.row.customer.firstname}` }}
								</el-link>
								<el-link type="primary" v-else @click="(e) => onTableCustomerCellClick(e, scope.row.customer)">
									{{ scope.row.customer.legalEntity.name }}
								</el-link>
								</template>
							</el-table-column>
							<el-table-column prop="vehicle" label="Автомобиль">
								<template #default="scope">
									<div v-if="scope.row.vehicle.govermentalNumber">
										{{ scope.row.vehicle.govermentalNumber }}, {{ `${scope.row.vehicle?.model?.manufacturer?.name} ${scope.row.vehicle?.model?.name}`}}						
									</div>
									<div v-else>
										{{ `${scope.row.vehicle?.model?.manufacturer?.name} ${scope.row.vehicle?.model?.name}`}}						
									</div>
									<div v-if="scope.row.vehicle.vin">
										<el-text type="info" size="small">
											{{ scope.row.vehicle.vin }}
										</el-text>
										&nbsp;
										<el-link type="primary" @click="(e) => onTableVINCopyClick(scope.row.vehicle.vin, e)" :underline="false">
											<el-icon><CopyDocument /></el-icon>
										</el-link>
									</div>
								</template>
							</el-table-column>
							<el-table-column :formatter="tableOrderTotalCostFormatter" label="Итого (руб.)" column-key="orderTotal" />
						<el-table-column width="100">
							<template #default="scope">
								<order-print-form-component :order-id="scope.row.id" icon-only />
							</template>
						</el-table-column>
					</el-table>	
				</template>
			</scrollview-component>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { h } from 'vue';
import { date as dateFormatter } from '@/helpers/formatters';
import { CirclePlus, CopyDocument } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElDatePicker, ElNotification } from 'element-plus';
import { Search } from '@element-plus/icons-vue';
import store from '@/store';
import TabsDictionary from '@/variables/tabs';
import EventHelper from '@/helpers/eventsHelper';
import OrderModel from '@/models/OrderModel';
import OrderStatusComponent from '@/components/OrderStatusComponent.vue';
import ApplicationTab from '@/models/ApplicationTab';
import EmployeesApiHelper from '@/helpers/api/EmployeesApiHelper';
import EmployeeModel from '@/models/EmployeeModel';
import orderStatusesHelper from '@/helpers/orderStatusHelper';
import CounterpartyModel from '@/models/CounterpartyModel';
import CounterpartyFilterComponent from '@/components/filters/CounterpartyFilterComponent.vue';
import ScrollviewComponent from '@/components/ScrollviewComponent.vue';
import OrderStatusSelectorComponent from '@/components/OrderStatusSelectorComponent.vue';
import OrderApiHelper from '@/helpers/api/OrderApiHelper';
import { IGetOrdersRequest } from '@/helpers/api/OrderApiHelper';
import OrderViewModel from '@/ViewModels/OrderViewModel';
import LegalEntityBranchModel from '@/models/LegalEntityBranchModel';
import LegalEntityApiHelper from '@/helpers/api/LegalEntityApiHelper';
import OrderPrintFormComponent from '@/components/order/OrderPrintFormComponent.vue';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink, Search, CopyDocument, ElDatePicker,
		OrderStatusComponent,
		ScrollviewComponent,
		CounterpartyFilterComponent,
		OrderStatusSelectorComponent,
		OrderPrintFormComponent
	}
})
export default class OrdersView extends Vue {
	/** constants */	
	refreshOrdersEvent = 'ordersListUpdate';	
	ordersHttpClient = new OrderApiHelper(store.getters.jwt);
	searchIcon = Search;
	
	predefinedDatesRangeFilters = [
		{ days: 1, label: 'сегодня', active: false },
		{ days: 30, label: 'месяц', active: true },
		{ days: 90, label: 'три месяца', active: false },
		{ days: 180, label: 'шесть месяцев', active: false },
	];
	statuses = orderStatusesHelper.statuses();
	statusesDescriptions = orderStatusesHelper.statusesDescriptionDict() as any;

	/** view-models */
	tableScollElement: Element | null = null;
	tableLoading = false;	
	tableSortColumn = 'date';
	tableSortDir: 'descending' | 'ascending' = 'descending';

	ordersList: Array<OrderViewModel> = [];
	ordersDateRangeFilter: Array<Date> = [];
	ordersSearchFilter: string | null = null;
	ordersManagersFilter: Array<number> = [];
	ordersStatusesFilter: Array<number> = [];		
	ordersCounterpartyFilter: number | null = null;
	ordersBranchesFilter: Array<number> = [];

	managers: Array<EmployeeModel> = [];
	employerBranches: Array<LegalEntityBranchModel> = [];

	mounted() {
		this.initView();
		EventHelper.setEventListener(this.refreshOrdersEvent, this.fetchOrders);
	}

	unmounted() {
		EventHelper.removeEventListener(this.refreshOrdersEvent, this.fetchOrders);
	}

	initView() {
		this.fetchManagers();
		this.fetchBranches()

		/** init date-range filter */
		this.predefinedDatesRangeFilters.forEach(i => {
			if (i.active) {
				this.setDateRangeFilter(i.days);
			}
		});

		this.fetchOrders(
			() => {
				const t = this.$refs.ordersTable as any;
				const tableElement = t.$el as HTMLElement;
				this.tableScollElement = tableElement.querySelector('.el-scrollbar__wrap .el-scrollbar__wrap--hidden-default')!;
			}
		);
	}

	onPredefinedDateRangeFilterClick(daysRange: number) {		
		this.predefinedDatesRangeFilters.forEach(i => {			
			i.active = daysRange == i.days;
			if (i.active){
				this.setDateRangeFilter(i.days);
			}
		});

		this.fetchOrders();
	}

	onCounterpartyFilterChanged(counterparty?: CounterpartyModel) {
		if (counterparty?.id) {
			this.ordersCounterpartyFilter = counterparty.id;
		} else {
			this.ordersCounterpartyFilter = null;
		}

		this.fetchOrders();
	}

	setDateRangeFilter(days: number) {
		const now = new Date();
		const endDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));

		switch(days) {
			case 1: {
				this.ordersDateRangeFilter = [
					new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)),
					endDate
				];
			}
			break;

			default: {
				const millisPerDay = 86400000;
				const startDate = new Date(now.getTime() - (days * millisPerDay));
				this.ordersDateRangeFilter = [
					new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0)),
					endDate
				];
			}
			break;
		}
	}
	
	onCreateOrderClick() {
		const appTab = TabsDictionary.orderAdd;
		store.commit('addTab', appTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableRowDblClick(row: OrderModel, target: Element, e: MouseEvent) {
		const targetEl = (e.target as Element);
		const sourceElementTagName = targetEl.tagName?.toLocaleLowerCase();

		if (sourceElementTagName != 'td' &&
			!targetEl.classList?.contains('cell')) {
			return;
		}

		const orderId = row.id;
		const orderViewTab = TabsDictionary.orderView;
		orderViewTab.params = [ orderId, true ];
		orderViewTab.allowMultiple = true;
		store.commit('addTab', orderViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableOrderNumberClick(row: OrderModel, e: MouseEvent) {
		e.stopPropagation();

		const orderId = row.id;
		const orderViewTab = TabsDictionary.orderView;
		orderViewTab.params = [ orderId, true ];
		orderViewTab.allowMultiple = true;
		store.commit('addTab', orderViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	dateFormatter(date: Date, showTime?: boolean) {
		return dateFormatter.dateTime(date, false, true, showTime, ', ');
	}

	async onTableVINCopyClick(vin: string, e: Event) {
		e.stopPropagation();
		if (vin.length) {
			await navigator.clipboard.writeText(vin);
		}
	}
	
	onTableOrderStatusChange(order: OrderViewModel, newStatusId: number) {				
		order.isAsyncOperationPending = true;		
		this.ordersHttpClient
			.setStatusAsync(order.id, newStatusId)
			.then(success => {
				if (success) {
					order.status = newStatusId;
					ElNotification({
						message: 'Статус заказ-наряда изменен',
						type: 'success'
					});
				} else {
					ElNotification({
						message: 'Ошибка изменения статуса',
						type: 'error',
					});
				}
			})
			.finally(() => order.isAsyncOperationPending = false);
	}

	fetchOrders(onLoadCallback?: Function) {
		this.tableLoading = true;			
		const r: IGetOrdersRequest = {
			orderBy: this.tableSortColumn,
			orderAsc: this.tableSortDir == 'ascending',
			searchQuery: this.ordersSearchFilter,
			periodStartDate: this.ordersDateRangeFilter.length != 2 ? null : this.ordersDateRangeFilter[0],
			periodEndDate: this.ordersDateRangeFilter.length != 2 ? null : this.ordersDateRangeFilter[1],
			statuses: this.ordersStatusesFilter,
			managers: this.ordersManagersFilter,
			branches: this.ordersBranchesFilter,
			counterpartyId: this.ordersCounterpartyFilter,
		};

		this.ordersHttpClient
			.getOrdersAsync(r, (_: any) => new OrderViewModel(_))
			.then((r: Array<OrderViewModel>) => {
				if (r === null) {
					ElNotification({
						message: 'Ошибка загрузки списка заказ-нарядов',
						type: 'error',
					});
					return;
				}

				this.ordersList = [];
				this.$nextTick(() => this.ordersList = r);
			})
			.finally(() => {				
				if (typeof onLoadCallback === 'function') {
					onLoadCallback();
				}
				this.tableLoading = false;
			});
	}

	onTableSortChange(eventObject: any) {
		this.tableSortColumn = eventObject.prop;
		this.tableSortDir = eventObject.order;
		this.fetchOrders();		
	}

	onTableManagerCellClick(e: Event, manager: EmployeeModel) {
		e.stopPropagation();

		this.ordersManagersFilter = [manager.id];
		this.fetchOrders();
	}

	onTableCustomerCellClick(e: Event, customer: CounterpartyModel) {
		e.stopPropagation();

		const counterpartyFilterComponent = this.$refs.counterpartyFilterComponent as any;
		this.ordersCounterpartyFilter = customer.id
		counterpartyFilterComponent.set(customer);

		this.fetchOrders();
	}

	tableOrderTotalCostFormatter(order: OrderModel) {
		return (order.customerItemsTotalCost + order.customerJobsTotalCost).toLocaleString('ru').replace(',', '.');
	}

	calculateTotal(param: any) {		
		const { columns, data } = param;	
		const totalColumn = columns.find((_: any) => _.columnKey == 'orderTotal');		
		if (!totalColumn) {
			return;
		}
		const totalColumnIndex: number = totalColumn.no;
		let total = 0;
		data.forEach((row: OrderModel) => total += Math.round(row.customerJobsTotalCost + row.customerItemsTotalCost));

		let totalRow = new Array<any>();

		for(let c = 0; c <= totalColumnIndex; c++) {
			if (c == totalColumnIndex) {
				const el = h('div', { style: { fontSize: '1.06rem' } }, [ total.toLocaleString('ru').replace(',', '.') ]);
				totalRow.push(el);
			} else {
				totalRow.push('');
			}
		}

		return totalRow;
	}

	fetchManagers() {
		new EmployeesApiHelper()
			.getManagersAsync(store.getters.jwt)
			.then(managers => this.managers = managers);
	}

	fetchBranches() {
		new LegalEntityApiHelper(store.getters.jwt)
			.getExecutiveBranchesAsync()
			.then(branches => {
				if (!branches) {
					this.employerBranches = [];
					return;
				}

				this.employerBranches = branches;
			});
	}
}
</script>

<style scoped>
.lr-margin-container {
	margin: .1rem 0
}
	.lr-margin-container a:first-child {
		margin-left: 0;
	}

	.lr-margin-container a {
		margin: 0 .33rem;
	}
</style>